import clsx from 'clsx'
import Button from 'lib/components/Button/Button'
import { PasswordInput, TextInput } from 'lib/components/Form'
import Header from 'lib/components/Header/Header'
import Link from 'lib/components/Link/Link'
import { SubBrandName } from 'lib/settings/brands/index'
import { IconNames } from 'lib/settings/icons'
import { emailPattern } from 'lib/utilities/string'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useModal } from '../../Modal/Modal'

interface SocialButtonProps {
  text: string
  icon: IconNames
  login: () => void
}

interface LoginModalIndexProps {
  onSubmit?: (data: Record<string, any>) => void
  login: () => void
  googleLogin: () => void
  facebookLogin: () => void
  appleLogin: () => void
  handleForgotLinkClick: React.MouseEventHandler<HTMLAnchorElement> | undefined
  handleOtpLinkClick: () => void
  defaultEmail?: string
  brand: SubBrandName
}

function SocialButton({ text, icon, login }: SocialButtonProps) {
  return (
    <Button
      icon={icon}
      overrideClassName
      className="flex items-center justify-center border border-neutral-700 rounded-lg w-full py-[18px] type-body-8 [&_span]:w-3 [&_span]:h-3"
      onClick={login}
    >
      {text}
    </Button>
  )
}

export default function LoginModalIndex({
  onSubmit,
  login,
  googleLogin,
  facebookLogin,
  appleLogin,
  handleForgotLinkClick,
  handleOtpLinkClick,
  defaultEmail = '',
  brand,
}: LoginModalIndexProps) {
  const { control, handleSubmit } = useForm()
  const [openDialog, hideDialog] = useModal((state) => [
    state.openDialog,
    state.hideDialog,
  ])

  const onFormSubmit = useCallback(
    (formData: Record<string, any>) => {
      onSubmit?.(formData)
      login()
    },
    [login, onSubmit],
  )

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div>
        <Header as="h2" className="type-body-3 mb-4">
          Log In
        </Header>
        <TextInput
          label="Email*"
          name="email"
          placeholder="Email address"
          className="mb-4"
          control={control}
          defaultValue={defaultEmail}
          rules={{
            required: { value: true, message: 'Email is required' },
            minLength: { value: 6, message: 'Email is too short' },
            pattern: {
              value: emailPattern,
              message: 'Please provide a valid email',
            },
          }}
        />
        <PasswordInput
          control={control}
          label="Password*"
          name="password"
          placeholder="Password"
          rules={{
            minLength: { value: 6, message: 'Password is too short' },
            required: { value: true, message: 'Password is required' },
          }}
        />
        <Link
          onClick={handleForgotLinkClick}
          className="lg:!type-body-9 !px-0 !h-[unset]"
        >
          Forgot password?
        </Link>
        <div className="mt-6">
          <Button type="submit" className="w-full">
            Log In
          </Button>
        </div>
      </div>
      <div className="overflow-hidden text-center my-2">
        <span
          className={clsx(
            'relative inline-block align-middle type-body-10',
            'before:w-[100vw] before:h-[1px] before:absolute before:top-1/2 before:left-[-8px] before:translate-x-[-100%] before:bg-neutral-300',
            'after:w-[100vw] after:h-[1px] after:absolute after:top-1/2 after:right-[-8px] after:translate-x-[100%] after:bg-neutral-300',
          )}
        >
          Or
        </span>
      </div>
      <div className="flex flex-col gap-2 mb-6">
        <Button
          overrideClassName
          className="flex items-center justify-center border border-neutral-700 rounded-lg w-full py-[18px] type-body-8"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            handleOtpLinkClick()
          }}
        >
          One-Time Password Sign In
        </Button>
        <SocialButton
          text="Google Sign In"
          icon="google-rounded"
          login={googleLogin}
        />
        <SocialButton
          text="Facebook Sign In"
          icon="facebook-rounded"
          login={facebookLogin}
        />
        {brand === 'shipsticks' && (
          <SocialButton
            text="Apple Sign In"
            icon="apple-rounded"
            login={appleLogin}
          />
        )}
      </div>
      <div className="flex gap-x-2 items-center">
        <span className="type-body-10">Don&apos;t have an account</span>
        <Link
          href="#"
          className="lg:!type-body-9 !px-0 !h-[unset]"
          onClick={(e) => {
            e.preventDefault()
            hideDialog('login-modal')
            openDialog('signup-modal')
          }}
        >
          Sign up here.
        </Link>
      </div>
    </form>
  )
}
